/*
 * 还款客户二级页面 (按平台分组的贷款列表)
 *
 * entityKey: mortgageLoan/listGroupPlatform -> GET /api/mis/mortgageLoan/listGroupPlatform?leadId=
 * Controller: MortgageLoanController.listGroupPlatform
 * Service: MortgageLoanServiceImpl.queryMortgageLoanGroupPlatform -> convertToTree (按平台分组)
 * Mapper: MortgageLoanMapper.selectMortgageLoanPage (附加还款统计数据)
 *
 * 主要功能:
 *  1. 按平台分组的贷款展示 (树形结构: 平台父节点 -> 贷款子节点 -> 汇总行 id=999999)
 *  2. 编辑贷款弹窗 (PUT /mortgageLoan)
 *  3. 删除贷款 (DELETE /mortgageLoan/delete/{id})
 *  4. 手动生成还款计划 (POST /mortgageLoan/manualMakeRepaymentPlan)
 *  5. 还款计划查看 (弹窗内嵌 RepaymentPlan 组件)
 *  6. 最新还款计划查询 (GET /mortgageLoan/selectLatestRepaymentPlan?loanId=)
 */
import { Col, ColType, Crud, CrudAction, CrudApi } from "../../comp/crud";
import {
  ApiClient,
  displayMortgageLoanStatus,
  displayMortgageLoanType,
  getLoanTypeItemOpt,
  getMortgageLoanStatusOpt,
  displayPaidMethod,
  displayRepaymentPlanStatus,
} from "../../api/ApiClient";
import React, { useEffect, useMemo, useState } from "react";
import {
  ArrayField,
  Button,
  Form,
  Modal,
  Tag,
  Toast,
  Tooltip,
  Typography,
  Row,
  Col as RowCol,
  Table,
  Divider,
  Skeleton,
  Empty,
  Space,
} from "@douyinfe/semi-ui";
import RepaymentPlan from "./repaymentPlan";
import {
  IconMinusCircle,
  IconModalStroked,
  IconPlusCircle,
  IconSidebar,
  IconChevronDown,
  IconRefresh,
} from "@douyinfe/semi-icons";
import {
  formatAmountZhCn,
  formatDate,
  renderPaidMethodTag,
  renderLoanStatusTag,
  hasValidChildren,
  renderRepaymentPlanStatusTag,
  
} from "../../utils";
import { FormApi } from "@douyinfe/semi-ui/lib/es/form";

let apiClient = ApiClient.getDefault();
const { Text } = Typography;

// 组件属性接口: leadId(客户ID), leadName(客户姓名), openMode(打开模式)
export interface MortgageLoanProps {
  leadId?: number;
  leadName?: string;
  openMode?: string;
}

// 主组件: 按平台分组的贷款列表页面，由一级页面(indexMortgageLead)点击贷款数或新窗口跳转进入
export default function IndexMortgageLoan(props: MortgageLoanProps) {
  const [planShow, setPlanShow] = useState(false);
  const [loanId, setLoanId] = useState(undefined as undefined | number);
  const [loanIds, setLoanIds] = useState(undefined as undefined | number[]);
  const [leadId, setLeadId] = useState(undefined as undefined | number);
  const [platform, setPlatform] = useState(undefined as undefined | string);
  const [paidType, setPaidType] = useState("all");
  const [loan, setLoan] = useState({} as any);
  const [crudApi, setCrudApi] = useState(undefined as undefined | CrudApi);
  const [makePlanModal, setMakePlanModal] = useState(false);
  const [makePlanFormApi, setMakePlanFormApi] = useState(
    undefined as undefined | FormApi
  );
  const [makePlanSaving, setMakePlanSaving] = useState(false);
  const role = localStorage.getItem("role");
  const [showMortgageField, setShowMortgageField] = useState(true);
  const [showCardField, setShowCardField] = useState(false);
  const [showBorrowField, setShowBorrowField] = useState(false);
  const [teamMembersTree, setTeamMembersTree] = useState([] as any[]);
  const [showSaveLoanModal, setShowSaveLoanModal] = useState(false);
  const [saveLoanFormApi, setSaveLoanFormApi] = useState(
    undefined as undefined | FormApi
  );
  const [selectedLeads, setSelectedLeads] = useState([] as any[]);
  const [saveLoanSaving, setSaveLoanSaving] = useState(false);
  const [mortgageLoan, setMortgageLoan] = useState(
    undefined as undefined | any
  );
  const [selectedPlanList, setSelectedPlanList] = useState([] as any);
  const [leadSummary, setLeadSummary] = useState(null as any);
  const [leadSummaryLoading, setLeadSummaryLoading] = useState(true);
  const [expandAllRows, setExpandAllRows] = useState(false);

  // ---- 渲染辅助函数 ----
  // 金额列统一渲染: 平台节点warning / 汇总行大号 / 普通行
  const renderAmount = (value: any, record: any) => {
    if (hasValidChildren(record)) {
      return (
        <Tooltip content={
          <div style={{ padding: '4px 0' }}>
            <div>贷款笔数: {record.children?.length}</div>
            <div>总金额: {formatAmountZhCn(value)}</div>
          </div>
        }>
          <Typography.Text strong type="warning" style={{ cursor: 'pointer' }}>{formatAmountZhCn(value)}</Typography.Text>
        </Tooltip>
      );
    } else if (record.platform == "汇总" && record.id == 999999) {
      return <Typography.Title heading={5}>{formatAmountZhCn(value)}</Typography.Title>;
    } else {
      return <Typography.Text>{formatAmountZhCn(value)}</Typography.Text>;
    }
  };

  // 可点击数字列统一渲染
  const renderClickable = (value: any, record: any, onClick: () => void) => {
    if (record.platform == "汇总" && record.id == 999999) {
      return <Typography.Title heading={5} link onClick={onClick}>{value}</Typography.Title>;
    }
    return <Typography.Text link onClick={onClick}>{value}</Typography.Text>;
  };


  // 贷款类型Tag (颜色区分)
  const renderLoanTypeTag = (value: any, record: any) => {
    if (hasValidChildren(record)) {
      return <Typography.Text strong type="warning">{displayMortgageLoanType(value)}</Typography.Text>;
    }
    const typeMap: Record<string, { label: string; color: string }> = {
      MORTGAGE: { label: "信贷", color: "blue" },
      CARD: { label: "信用卡", color: "violet" },
      BORROW: { label: "借款", color: "orange" },
    };
    const info:any = typeMap[value];
    return info ? <Tag color={info.color} size="small" type="solid">{info.label}</Tag> : <span>{displayMortgageLoanType(value)}</span>;
  };

 const mortgageLoanPaidMethodOpt = ()=> {
   
      return [{
        label: "代还",
        value: "DH",
        key: "DH",
      },
      {
        label: "客户自还",
        value: "KHZH",
        key: "KHZH",
      }];
   
  }

  // 列定义: Crud 表格的列配置，包含贷款平台、金额、还款计划数、还款笔数、状态等
  // 树形展示: 平台父节点(warning色) -> 贷款子节点 -> 汇总行(id=999999, heading样式)
  let cols: Col[] = [
    {
      name: "ID",
      key: "id",
      hideInEdit: true,
      hideInCreate: true,
      hideInList: true,
      type: ColType.NUMBER,
    },
    {
      name: "贷款平台",
      key: "platform",
      width: 150,
      type: ColType.STRING,
      showInFilter: true,
      render: (value, record) => {
        if (hasValidChildren(record)) {
          return (
            <Typography.Text strong type="warning">
              {value}
              <Text type="quaternary" style={{ fontSize: 11, marginLeft: 4 }}>
                ({record.children?.length ?? 0}笔)
              </Text>
            </Typography.Text>
          );
        } else if (record.platform == "汇总" && record.id == 999999) {
          return <Typography.Text strong style={{ fontSize: 18 }}>{value}</Typography.Text>;
        } else {
          return <Typography.Text strong type="primary">{value}</Typography.Text>;
        }
      },
    },
    {
      name: "客户ID",
      key: "leadId",
      width: 80,
      showInFilter: false,
      type: ColType.NUMBER,
      hideInEdit: true,
      hideInList: true,
    },
    {
      name: "客户姓名",
      key: "leadName",
      width: 80,
      type: ColType.STRING,
      hideInEdit: true,
      hideInCreate: true,
      showInFilter: false,
      hideInList: true,
    },
    {
      name: "城市",
      key: "leadCity",
      width: 40,
      type: ColType.SELECT,
      getOptions: () => apiClient.getDictAsOpts("city"),
      hideInEdit: true,
      hideInCreate: true,
      hideInList: true,
      showInFilter: false,
    },
    {
      name: "按揭专员ID",
      key: "agentId",
      type: ColType.STRING,
      showInFilter: false,
      hideInList: true,
      hideInCreate: true,
      hideInEdit: true,
    },
    {
      name: "按揭专员",
      key: "agentName",
      width: 80,
      type: ColType.STRING,
      showInFilter: false,
      hideInCreate: true,
      hideInEdit: true,
      hideInList: true,
    },
    {
      name: "销售ID",
      key: "saleId",
      type: ColType.STRING,
      showInFilter: false,
      hideInList: true,
      hideInCreate: true,
      hideInEdit: true,
    },
    {
      name: "销售",
      key: "saleName",
      width: 80,
      type: ColType.STRING,
      showInFilter: false,
      hideInCreate: true,
      hideInEdit: true,
      hideInList: true,
    },
    {
      name: "贷款类型",
      key: "loanType",
      width: 80,
      type: ColType.SELECT,
      showInFilter: true,
      getOptions: () => Promise.resolve(getLoanTypeItemOpt()),
      render: (value, record) => renderLoanTypeTag(value, record),
    },
    {
      name: "贷款金额",
      key: "amount",
      width: 90,
      type: ColType.NUMBER,
      render: (value, record) => renderAmount(value, record),
    },
    {
      name: "月供金额",
      key: "oneTermAmount",
      width: 90,
      type: ColType.NUMBER,
      optional: true,
      helpText: "信贷客户必须填写月供金额，信用卡客户填0",
      render: (value, record) => renderAmount(value, record),
    },
    {
      name: "期数",
      key: "term",
      width: 50,
      type: ColType.STRING,
      showInFilter: false,
      hideInCreate: true,
      hideInEdit: true,
    },
    {
      name: "还款计划",
      key: "totalTerm",
      width: 70,
      type: ColType.NUMBER,
      showInFilter: false,
      hideInCreate: true,
      hideInEdit: true,
      render: (value, record) => renderClickable(value, record, () => {
        if (hasValidChildren(record)) {
          openRepaymentPlanModal(undefined, record.leadId, record.platform, "all");
        } else if (record.platform == "汇总" && record.id == 999999) {
          openRepaymentPlanModal(undefined, record.leadId, undefined, "all");
        } else {
          openRepaymentPlanModal(record.id, record.leadId, undefined, "all");
        }
      }),
    },
    {
      name: "还款笔数",
      key: "paidTerm",
      width: 70,
      type: ColType.NUMBER,
      showInFilter: false,
      hideInCreate: true,
      hideInEdit: true,
      render: (value, record) => renderClickable(value, record, () => {
        if (hasValidChildren(record)) {
          openRepaymentPlanModal(undefined, record.leadId, record.platform, "YJQ");
        } else if (record.platform == "汇总" && record.id == 999999) {
          openRepaymentPlanModal(undefined, record.leadId, undefined, "YJQ");
        } else {
          openRepaymentPlanModal(record.id, record.leadId, undefined, "YJQ");
        }
      }),
    },
    {
      name: "还款进度",
      key: "repaymentProgress",
      type: ColType.STRING,
      width: 100,
      showInFilter: false,
      hideInCreate: true,
      hideInEdit: true,
      render: (value, record) => {
        const paid = record.paidTerm || 0;
        const total = record.totalTerm || 0;
        const pct = total > 0 ? Math.round(paid / total * 100) : 0;
        if (record.platform == "汇总" && record.id == 999999 && total === 0) {
          return <Text type="quaternary">-</Text>;
        }
        const color = pct >= 100 ? '#52c41a' : pct >= 50 ? '#fa8c16' : '#e91e63';
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 50, height: 6, borderRadius: 3, background: 'var(--semi-color-fill-1)' }}>
              <div style={{ width: `${pct}%`, height: '100%', borderRadius: 3, background: color, transition: 'width 0.3s' }} />
            </div>
            <Text style={{ color, fontWeight: 600, fontSize: 12 }}>{pct}%</Text>
          </div>
        );
      },
    },
    {
      name: "实还金额",
      key: "paidAmount",
      width: 90,
      type: ColType.NUMBER,
      hideInEdit: true,
      hideInCreate: true,
      render: (value, record) => renderAmount(value, record),
    },
    {
      name: "还款日",
      key: "firstPayDate",
      width: 90,
      type: ColType.DATE,
    },
    {
      name: "状态",
      key: "status",
      width: 80,
      render: (value, record) => renderLoanStatusTag(value, record),
      type: ColType.SELECT,
      showInFilter: true,
      hideInEdit: true,
      hideInCreate: true,
      getOptions: () => Promise.resolve(getMortgageLoanStatusOpt()),
    },
    {
      name: "账单日",
      key: "billDay",
      width: 50,
      type: ColType.NUMBER,
      optional: true,
      render: (value, record) => {
        if (record.platform == "汇总") return null;
        return value;
      },
    },
    {
      name: "借款日期",
      key: "borrowDate",
      width: 90,
      type: ColType.DATE,
    },
    {
      name: "出借人",
      key: "borrowerName",
      width: 80,
      type: ColType.STRING,
    },
    {
      name: "还款方式",
      key: "paidMethod",
      width: 90,
      type: ColType.SELECT,
      showInFilter: true,
      hideInEdit: true,
      hideInCreate: true,
      getOptions: () => Promise.resolve(mortgageLoanPaidMethodOpt()),
      render: (value) => renderPaidMethodTag(value),
    },
    {
      name: "结清日期",
      key: "settleTime",
      type: ColType.DATE,
      hideInEdit: true,
      hideInCreate: true,
      width: 100,
      defaultSortOrder: "descend",
    },
    {
      name: "贷款编号",
      key: "loanNo",
      type: ColType.STRING,
      showInFilter: true,
      hideInCreate: true,
      hideInEdit: true,
      width: 110,
    },
  ];

  if (props.openMode == "new") {
    window.document.title =
      "客户贷款:" + props.leadName + "(" + props.leadId + ")";
  }

  // 页面加载时获取团队成员（销售和按揭专员）树形选项，用于编辑贷款时选择出款人
  useEffect(() => {
    //获取所有销售和按揭专员
    apiClient
      .getTeamMemberByDataScopeAsOpts("payment")
      .then((opts) => setTeamMembersTree(opts));
    return () => {
      console.log("离开页面------------");
    };
  }, []);

  // 页面加载时获取客户贷款汇总信息，用于顶部展示
  useEffect(() => {
    if (props.leadId) {
      setLeadSummaryLoading(true);
      apiClient.request("/mortgageLoan/leadSummary", "GET", { leadId: props.leadId }).then((resp: any) => {
        setLeadSummary(resp.data);
        setLeadSummaryLoading(false);
      }).catch(() => {
        setLeadSummaryLoading(false);
      });
    }
  }, [props.leadId]);

  // 功能2: 编辑贷款按钮处理
  // 根据贷款类型(MORTGAGE/CARD/BORROW)切换表单字段，打开编辑贷款弹窗
  const handleSaveLoanButtonClick = async (row: any) => {
    if (row.loanType === "MORTGAGE") {
      setShowMortgageField(true);
      setShowCardField(false);
      setShowBorrowField(false);
    } else if (row.loanType === "CARD") {
      setShowCardField(true);
      setShowMortgageField(false);
      setShowBorrowField(false);
    } else {
      setShowBorrowField(true);
      setShowMortgageField(false);
      setShowCardField(false);
    }
    apiClient.getLead(row.leadId).then((resp) => {
      row.leadName = resp.name;
      row.applyAmount = resp.applyAmount;
      setMortgageLoan(row);
      setShowSaveLoanModal(true);
    });
  };

  // 编辑动作定义
  const editAction: CrudAction = {
    name: "编辑",
    type:"warning",
    action: handleSaveLoanButtonClick,
  };

  // 功能2: 编辑贷款提交
  // API: PUT /mortgageLoan
  // Controller: MortgageLoanController.update
  // 校验: 金额>0, 信贷必须填月供金额, 个人借款必须填借款日期
  const handleSaveLoanClick = async (optType: string) => {
    try {
      if (saveLoanFormApi!.getValue("loanType") == "CARD") {
        saveLoanFormApi!.setValue("oneTermAmount", 0);
      }
      await saveLoanFormApi!.validate();
      let values = saveLoanFormApi!.getValues();
      values["id"] = mortgageLoan?.id; //更新必须要传ID
      let leadId = values["leadId"];
      if (values["amount"] <= 0) {
        Toast.info("贷款（借款）金额必须大于0");
        return;
      }
      //校验oneTermAmount字段"贷款类型为信贷客户必须填写月供金额，信用卡客户不用填"
      if (
        values["loanType"] == "MORTGAGE" &&
        (!values["oneTermAmount"] || values["oneTermAmount"] <= 0)
      ) {
        Toast.info("请填写月供金额且月供金额大于0");
        return;
      }
      if (values["loanType"] == "BORROW" && !values["borrowDate"]) {
        Toast.info("请填写借款日期");
        return;
      }

      values["lastQueryTime"] = formatDate(values["lastQueryTime"]);
      values["firstPayDate"] = formatDate(values["firstPayDate"]);
      values["borrowDate"] = formatDate(values["borrowDate"]);
      Modal.confirm({
        title: "是否确认修改贷款信息。",
        content: "",
        onOk: async () => {
          try {
            setSaveLoanSaving(true);
            await apiClient.request("/mortgageLoan", "PUT", undefined, values);
            Toast.info("保存贷款信息成功");
            if (props.leadId) {
              apiClient.request("/mortgageLoan/leadSummary", "GET", { leadId: props.leadId }).then((resp: any) => {
                setLeadSummary(resp.data);
              });
            }
            if (optType == "update") {
              setSelectedLeads([]);
              crudApi?.refresh();
              setSaveLoanSaving(false);
              setShowSaveLoanModal(false);
            } else {
              saveLoanFormApi?.reset();
              saveLoanFormApi?.setValue("leadId", leadId);
            }
          } catch (e) {
            setSaveLoanSaving(false);
          }
        },
      });
    } catch (e) {
      setSaveLoanSaving(false);
    }
  };

  // 功能3: 删除贷款
  // API: DELETE /mortgageLoan/delete/{id}  (通过 apiClient.deleteMortgageLoan 调用)
  // Controller: MortgageLoanController.delete
  // 同时删除关联的所有还款计划
  const deleteMortgageLoan = (row: any) => {
    Modal.confirm({
      title: "是否确认删除？该操作会删除客户所有的还款计划。",
      content: "",
      onOk: async () => {
        try {
          await apiClient.deleteMortgageLoan(row.id);
          Toast.info("删除成功");

          crudApi?.refresh();
        } catch (e) {}
      },
    });
  };

  // 删除动作定义
  const deleteAction: CrudAction = {
    name: "删除",
    type:"danger",
    action: deleteMortgageLoan,
  };

  // 功能4: 手动生成还款计划 - 打开弹窗
  // 先查询已有还款计划: GET /repaymentPlan?loanId={loan.id}
  // 然后在弹窗中展示已有计划，允许手动新增计划行
  const makeRepaymentPlan = async (loan: any) => {
    setLoan(loan);
    //调用接口查询还款计划
    apiClient
      .request("/repaymentPlan", "GET", {
        page: 1,
        size: 100,
        loanId: loan.id,
        statusOrderBy: 0,
      })
      .then((resp: any) => {
        setSelectedPlanList(resp.data.records);
        setMakePlanModal(true);
      });
  };

  // 功能4: 手动生成还款计划提交
  // API: POST /mortgageLoan/manualMakeRepaymentPlan
  // Controller: MortgageLoanController.manualMakeRepaymentPlan
  // 参数: { id(贷款ID), planDtos(还款计划DTO数组) }
  const handleMakeRepaymentPlan = async () => {
    await makePlanFormApi!.validate();
    let values = makePlanFormApi!.getValues();
    //如果为空，则不处理
    if (!values.rules || values.rules.length == 0) {
      Toast.info("请填写还款计划！");
      return;
    }
    //校验oneTermAmount字段是否<=0
    for (let item of values.rules) {
      if (item.oneTermAmount <= 0) {
        Toast.info("请填写计划应还金额且计划应还金额大于0");
        return;
      }
    }
    Modal.confirm({
      title: "是否确认新增还款计划。",
      content: "",
      onOk: async () => {
        try {
          let params = {
            id: values.id,
            planDtos: values.rules,
          };

          try {
            setMakePlanSaving(true);
            await apiClient.request(
              "/mortgageLoan/manualMakeRepaymentPlan",
              "POST",
              undefined,
              params
            );
            Toast.info("新增还款计划成功");
            if (props.leadId) {
              apiClient.request("/mortgageLoan/leadSummary", "GET", { leadId: props.leadId }).then((resp: any) => {
                setLeadSummary(resp.data);
              });
            }
            setLoan({});
            crudApi?.refresh();
            setMakePlanSaving(false);
            setMakePlanModal(false);

            crudApi?.refresh();
          } catch (e) {
            setMakePlanSaving(false);
          }
        } catch (e) {}
      },
    });
  };

  const makePlanAction: CrudAction = {
    name: "新增还款计划",
    action: makeRepaymentPlan,
  };

  // 功能5: 打开还款计划弹窗
  // 弹窗内嵌 RepaymentPlan 组件，支持按贷款ID、客户ID、平台、还款状态过滤
  // 当 id 和 platform 都为空时，收集所有非汇总行的贷款ID列表传入
  const openRepaymentPlanModal = async (
    id: number | undefined,
    leadId: number | undefined,
    platform: string | undefined,
    paidType: string
  ) => {
    setLoanId(id);
    setLeadId(leadId);
    setPlatform(platform);
    setPaidType(paidType);
    if (!id && !platform) {
      let loans: any = crudApi?.getData().filter((item) => item.id != 999999);
      let ids: any = [];
      loans.forEach((item: any) => {
        item.children.map((itemN: any) => ids.push(itemN.id));
      });
      setLoanIds(ids);
    }
    setPlanShow(true);
  };

  // 处理手动还款计划弹窗中的表单提交
  // type="add"/"blurAdd": 新增或更新一行还款计划到 selectedPlanList
  // type="remove"/"clean": 从 selectedPlanList 中移除指定行
  // 校验: 还款日期不能重复，计划金额必须>0
  // 处理表单提交
  const handleFormSubmit = async (type: any, field: any) => {
    let newPlans: any[] = [];

    if ("add" == type || "blurAdd" == type) {
      let dueDate = makePlanFormApi!.getValue(field+"[dueDate]");
      let oneTermAmount = makePlanFormApi!.getValue(field+"[oneTermAmount]");
      let borrowPlanStartDate = makePlanFormApi!.getValue(field+"[borrowPlanStartDate]");
      dueDate = formatDate(dueDate);
      if(borrowPlanStartDate){
         borrowPlanStartDate = formatDate(borrowPlanStartDate);
      }
      if ("add" == type) {
        //最后两位替换为payDay
        dueDate = dueDate.substring(0, dueDate.length - 2) + loan.payDay;
      }

      //判断还款日期dueDate是否存在selectedPlanList中
      if (selectedPlanList) {
        let newPlans: any = [...selectedPlanList];
        let valid = 0;
        newPlans.forEach((item: any) => {
          if (item.dueDate === dueDate && !item.field) {
            valid = 1;
          } else if (
            
            item.field &&
            item.field === field
          ) {
            valid = 2; //更新
            item.dueDate = dueDate;
            item.oneTermAmount = oneTermAmount;
            item.borrowPlanStartDate = borrowPlanStartDate;
          }  else if (
            item.dueDate === dueDate &&
            item.field &&
            item.field != field
          ) {
            valid = 4; //重复
          }
        });
        if (valid == 1 || valid == 4) {
          Toast.warning("该还款日期已存在");
          return;
        }
        if (valid == 2) {
          setSelectedPlanList(newPlans);
        //   Toast.success("已更新还款日期");
          return;
        }
        if (valid == 3) {
          return;
        }
      }

      //取selectedLoanList集合里对象的Id最大的
      let maxId = 0;
      let maxPeriodNo = 1;
      if (selectedPlanList && selectedPlanList.length > 0) {
        maxId = findMaxId(selectedPlanList);
        maxPeriodNo = findMaxPeriodNo(selectedPlanList);
      }
      let id = maxId + 1;
      let periodNo = maxPeriodNo + 1;
      const addData = {
        ...loan,
        id: id,
        paidAmount: 0,
        periodNo: periodNo,
        dueDate: dueDate,
        oneTermAmount: oneTermAmount,
        borrowPlanStartDate:borrowPlanStartDate,
        status: "HKZ",
        field: field,
      };
      newPlans = [...selectedPlanList, addData];
      setSelectedPlanList(newPlans);
    } else if ("remove" == type || "clean" == type) {
      //移除不存在的

      const filteredPlans = selectedPlanList.filter(
        (plan: any) => plan.field != field
      );
      setSelectedPlanList(filteredPlans);
    }
  };

  // 工具函数: 查找数组中对象id的最大值（用于新增还款计划时生成临时ID）
  function findMaxId(items: any[]): any {
    if (items.length === 0) return null;

    return items.reduce(
      (maxId, item) => Math.max(maxId, item.id),
      items[0].id // 初始值设为第一个元素的ID
    );
  }

  // 工具函数: 查找数组中对象periodNo的最大值（用于新增还款计划时递增期数）
  function findMaxPeriodNo(items: any[]): any {
    if (items.length === 0) return null;

    return items.reduce(
      (max, item) => Math.max(max, item.periodNo),
      items[0].periodNo // 初始值设为第一个元素的ID
    );
  }

  // 计算手动还款计划列表的汇总数据（贷款金额、应还金额、实还金额合计）
  // 计算汇总数据
  const summary = useMemo(() => {
    if (selectedPlanList && selectedPlanList.length > 0) {
      return {
        amount: selectedPlanList[0].amount,
        oneTermAmount: safeSumBy(selectedPlanList, "oneTermAmount"),
        paidAmount: safeSumBy(selectedPlanList, "paidAmount"),
        postingAmount: safeSumBy(selectedPlanList, "postingAmount"),
      };
    } else {
      return {
        amount: 0,
        oneTermAmount: 0,
        paidAmount: 0,
      };
    }
  }, [selectedPlanList]);

  /**
   * 计算属性安全总和
   * @param items 对象数组
   * @param prop 数字类型的属性名
   * @returns 有效数字的总和
   */
  function safeSumBy<T>(items: T[], prop: keyof T): number {
    return items.reduce((sum, item) => {
      const value = item[prop];

      // 类型安全验证
      if (typeof value === "number" && !isNaN(value)) {
        return sum + value;
      }

      // 尝试转换为数字
      const numericValue = Number(value);
      return !isNaN(numericValue) ? sum + numericValue : sum;
    }, 0);
  }
  // 合并还款计划数据和汇总行（id=999999），用于手动还款计划弹窗右侧表格展示
  // 合并数据和汇总行
  const dataWithSummary = [
    {
      key: "summary",
      periodNo: "汇总",
      id: 999999,
      leadId: 999999,
      amount: summary.amount,
      oneTermAmount: summary.oneTermAmount,
      paidAmount: summary.paidAmount,
      postingAmount: summary.postingAmount,
    },
    ...selectedPlanList,
  ];

  // 工具函数: 将日期增加一个月，用于手动新增还款计划时自动计算下一期还款日期
  //日期增加一个月
  const addMonth = (date: Date) => {
    date.setMonth(date.getMonth() + 1);
    return formatDate(date);
  };

  return (
    <>
      <Modal
        icon={
          <IconModalStroked size={"extra-large"} style={{ color: "blue" }} />
        }
        style={{ width: 720 }}
        bodyStyle={{ maxHeight: 580, overflowY: 'auto' }}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>编辑贷款</span>
            {mortgageLoan?.loanType && (
              <Tag
                color={mortgageLoan.loanType === 'MORTGAGE' ? 'blue' : mortgageLoan.loanType === 'CARD' ? 'violet' : 'orange'}
                size="small"
                type="solid"
              >
                {displayMortgageLoanType(mortgageLoan.loanType)}
              </Tag>
            )}
          </div>
        }
        visible={showSaveLoanModal}
        confirmLoading={saveLoanSaving}
        onCancel={() => setShowSaveLoanModal(false)}
        footer={
          <div>
            <Button type="tertiary" onClick={() => setShowSaveLoanModal(false)}>
              取消
            </Button>
            <Button
              type="primary"
              onClick={() => handleSaveLoanClick("update")}
            >
              保存
            </Button>
          </div>
        }
      >
        <Form
          getFormApi={(formApi) => setSaveLoanFormApi(formApi)}
          labelPosition="left"
          labelWidth={130}
          labelAlign="right"
        >
          {/* 客户信息展示条 */}
          <div style={{
            display: 'flex', gap: 12, padding: '8px 12px', marginBottom: 8,
            background: 'var(--semi-color-fill-0)', borderRadius: 4
          }}>
            <Text type="tertiary">客户: <Text strong>{mortgageLoan?.leadName}</Text></Text>
            {mortgageLoan?.applyAmount && (
              <Text type="tertiary">签约金额: <Text strong>{formatAmountZhCn(mortgageLoan.applyAmount)}</Text></Text>
            )}
          </div>
          <Form.InputNumber
            rules={[{ required: true, message: "请填写" }]}
            key="leadId"
            label="客戶ID"
            initValue={mortgageLoan?.leadId}
            field="leadId"
            readonly
            
          />
          <Form.Select
            rules={[{ required: true, message: "请填写" }]}
            key="loanType"
            label="贷款类型"
            disabled
            style={{ width: 200 }}
            field="loanType"
            initValue={mortgageLoan?.loanType}
            optionList={getLoanTypeItemOpt().map((o) => {
              return {
                label: o.label,
                value: o.value,
                disabled: o.disabled,
              };
            })}
            onChange={(value) => {
              if (value === "MORTGAGE") {
                setShowMortgageField(true);
                setShowCardField(false);
                setShowBorrowField(false);
              } else if (value === "CARD") {
                setShowCardField(true);
                setShowMortgageField(false);
                setShowBorrowField(false);
              } else {
                setShowBorrowField(true);
                setShowMortgageField(false);
                setShowCardField(false);
              }
            }}
          />

          {/* 如果是"贷款" */}
          {showMortgageField && (
            <div>
              <Form.Section text="贷款信息">
                <Form.Input
                  rules={[{ required: true, message: "请填写" }]}
                  key="platform"
                  label="贷款平台"
                  style={{ width: 280 }}
                  field="platform"
                  initValue={mortgageLoan?.platform}
                ></Form.Input>
                <Form.InputNumber
                  rules={[{ required: true, message: "请填写" }]}
                  key="amount"
                  label="贷款金额"
                  field="amount"
                  initValue={mortgageLoan?.amount}
                />
                <Form.InputNumber
                  rules={[{ required: true, message: "请填写" }]}
                  key="term"
                  label="按揭期数"
                  field="term"
                  initValue={mortgageLoan?.term}
                  disabled
                />
              </Form.Section>
              <Form.Section text="还款信息">
                <Form.InputNumber
                  rules={[{ required: true, message: "请填写" }]}
                  key="oneTermAmount"
                  label="月供金额"
                  field="oneTermAmount"
                  initValue={mortgageLoan?.oneTermAmount}
                />
                <Form.DatePicker
                  rules={[{ required: true, message: "请选择日期" }]}
                  key="firstPayDate"
                  type="date"
                  label="首次还款日"
                  field="firstPayDate"
                  initValue={mortgageLoan?.firstPayDate}
                />
                <Form.RadioGroup
                  field="paidMethod"
                  initValue={mortgageLoan?.paidMethod || "DH"}
                  rules={[{ required: true, message: "请选择还款方式" }]}
                  label="还款方式"
                >
                  <Form.Radio value="DH">代还</Form.Radio>
                  <Form.Radio value="KHZH">客户自还</Form.Radio>
                </Form.RadioGroup>
              </Form.Section>
            </div>
          )}

          {/* 如果是"信用卡" */}
          {showCardField && (
            <div>
              <Form.Section text="贷款信息">
                <Form.Input
                  rules={[{ required: true, message: "请填写" }]}
                  key="platform"
                  label="贷款平台"
                  style={{ width: 280 }}
                  field="platform"
                  initValue={mortgageLoan?.platform}
                ></Form.Input>
                <Form.InputNumber
                  rules={[{ required: true, message: "请填写" }]}
                  key="amount"
                  label="贷款金额"
                  field="amount"
                  initValue={mortgageLoan?.amount}
                />
              </Form.Section>
              <Form.Section text="还款信息">
                <Form.DatePicker
                  rules={[{ required: true, message: "请选择日期" }]}
                  key="firstPayDate"
                  type="date"
                  label="首次还款日"
                  field="firstPayDate"
                  initValue={mortgageLoan?.firstPayDate}
                />
                <Form.InputNumber
                  key="billDay"
                  label="账单日"
                  field="billDay"
                  initValue={mortgageLoan?.billDay}
                />
                <Form.RadioGroup
                  field="paidMethod"
                  initValue={mortgageLoan?.paidMethod}
                  rules={[{ required: true, message: "请选择还款方式" }]}
                  label="还款方式"
                >
                  <Form.Radio value="DH">代还</Form.Radio>
                  <Form.Radio value="KHZH">客户自还</Form.Radio>
                </Form.RadioGroup>
              </Form.Section>
            </div>
          )}

          {/* 如果是"个人借款" */}
          {showBorrowField && (
            <div>
              <Form.Section text="出借信息">
                <Form.Select
                  placeholder="请选择出款人"
                  rules={[{ required: true, message: "请选择出款人" }]}
                  label={"出款人"}
                  field="borrowerId"
                  optionList={teamMembersTree}
                  showClear={true}
                  filter={true}
                  style={{ width: 300 }}
                  initValue={mortgageLoan?.borrowerId}
                ></Form.Select>
                 <Form.InputNumber
                  rules={[{ required: true, message: "请填写" }]}
                  key="amount"
                  label="出借金额"
                  field="amount"
                  initValue={mortgageLoan?.amount}
                />
                <Form.DatePicker
                  rules={[{ required: true, message: "请选择日期" }]}
                  key="borrowDate"
                  type="date"
                  label="借款日期"
                  field="borrowDate"
                  initValue={mortgageLoan?.borrowDate}
                />
              </Form.Section>
              <Form.Section text="还款信息">
               
                <Form.DatePicker
                  rules={[{ required: true, message: "请选择日期" }]}
                  key="firstPayDate"
                  type="date"
                  label="约定还款日"
                  field="firstPayDate"
                  initValue={mortgageLoan?.firstPayDate}
                />
              </Form.Section>
            </div>
          )}

          {/* <Form.DatePicker
            key="lastQueryTime"
            type="date"
            label="最后查询日期"
            field="lastQueryTime"
          /> */}
        </Form>
      </Modal>

      <Modal
        icon={
          <IconModalStroked size={"extra-large"} style={{ color: "blue" }} />
        }
        style={{ width: 1600, height: 750, overflowX: "auto" }}
        title="生成还款计划"
        visible={makePlanModal}
        okText="批量生成"
        confirmLoading={makePlanSaving}
        onOk={handleMakeRepaymentPlan}
        onCancel={() => setMakePlanModal(false)}
      >
        <Form
          getFormApi={(formApi) => setMakePlanFormApi(formApi)}
          labelPosition="left"
          labelWidth={100}
          labelAlign="right"
          style={{ height: 550, overflowY: "auto" }}
        >
          <Row style={{ overflowX: "auto" }}>
            <RowCol span={10} style={{ overflowX: "auto" }}>
              <div style={{ display: "none" }}>
                <Form.Input
                  rules={[{ required: true, message: "请填写" }]}
                  key="id"
                  noLabel={true}
                  initValue={loan?.id}
                  disabled={true}
                  field="id"
                />
              </div>
              {/* 贷款上下文卡片 */}
               <Form.Input
                rules={[{ required: true, message: "请填写" }]}
                key="platform"
                label="贷款平台"
                initValue={loan?.platform}
                disabled={true}
                field="platform"
                width={100}
              />
              <div style={{
                padding: '8px 12px', marginBottom: 8, borderRadius: 4,
                background: 'var(--semi-color-fill-0)',
                borderLeft: '3px solid #1677ff',
              }}>
               
                <div style={{ fontSize: 14 }}>
                  贷款金额: <Tag color="blue" size="large" shape="circle" type="solid">{formatAmountZhCn(loan?.amount)}</Tag>
                   &nbsp;&nbsp;&nbsp;&nbsp; 月供: <Tag color="blue" size="large" shape="circle" type="solid">{formatAmountZhCn(loan?.oneTermAmount)}</Tag>
                  &nbsp;&nbsp;&nbsp;&nbsp;期数: <Tag color="blue" size="large" shape="circle" type="solid">{loan?.term}期</Tag>
                </div>
              </div>
             
              <Form.Section text={"计划信息"}>
                <ArrayField field="rules">
                  {({ add, arrayFields, addWithInitValue }) => (
                    <React.Fragment>
                      <Button
                        icon={<IconPlusCircle />}
                        type="primary"
                        theme="solid"
                        onClick={async () => {
                          //找到selectedPlanList中最大的还款日期
                          if (selectedPlanList.length > 0) {
                            const maxRepaymentDate = selectedPlanList.reduce(
                              (prev: any, curr: any) => {
                                return prev.dueDate > curr.dueDate
                                  ? prev
                                  : curr;
                              }
                            ).dueDate;
                            //日期增加一个月
                            const newDate = addMonth(
                              new Date(maxRepaymentDate)
                            );
                            let oneTermAmount = 0;
                            let borrowPlanStartDate;
                            if(loan?.loanType === "BORROW" || loan?.loanType === "CARD"){
                                //通过接口获取借款金额 /selectLatestRepaymentPlan
                                let res:any = await apiClient.request("/mortgageLoan/selectLatestRepaymentPlan?loanId="+loan?.id, "get");
                                console.log(res);
                                oneTermAmount = res.data.oneTermAmount;
                                borrowPlanStartDate = res.data.borrowPlanStartDate;
                            }else{
                                oneTermAmount = loan?.oneTermAmount;

                            }
                            addWithInitValue({ dueDate: newDate, oneTermAmount: oneTermAmount,borrowPlanStartDate:borrowPlanStartDate });

                            let field =
                              "rules[" + String(arrayFields.length) + "]";
                            console.log(field);
                            handleFormSubmit("add", field);
                          } else {
                            add();
                          }
                        }}
                        style={{ marginLeft: 8, marginBottom: 8 }}
                      >
                        新增还款计划
                      </Button>
                      {arrayFields.map(({ field, key, remove }, i) => (
                        <div key={key} style={{
                          width: 500,
                          padding: '4px',
                          marginBottom: 4,
                          borderRadius: 4,
                          border: '1px dashed var(--semi-color-success)',
                          background: 'rgba(227, 249, 236, 0.4)',
                        }}>
                          <Row>

                            <RowCol span={1}>
                                <Button
                            type="danger"
                            theme="borderless"
                            icon={<IconMinusCircle />}
                            onClick={(value) => {
                              if (arrayFields.length - 1 != i) {
                                Toast.info(
                                  "不能从中间删除日期，请按顺序操作，先删除后面的日期"
                                );
                                return;
                              }

                              remove();
                              handleFormSubmit("remove", `${field}`);
                            }}
                            style={{ marginTop: 9 }}
                          ></Button>
                            </RowCol>
                            <RowCol span={12}>
                              <Form.DatePicker
                                rules={[
                                  { required: true, message: "请选择还款日期" },
                                ]}
                                type="date"
                                label="还款日期"
                                field={`${field}.dueDate`}
                                onBlur={() =>
                                  handleFormSubmit("blurAdd", `${field}`)
                                }
                                style={{ width: 120 }}
                              />
                            </RowCol>
                            <RowCol span={11}>
                              <Form.Input
                                rules={[
                                  {
                                    required: true,
                                    message: "请填写计划还款金额",
                                  },
                                ]}
                                type="number"
                                label="计划金额"
                                field={`${field}.oneTermAmount`}
                                style={{ width: 120 }}
                                onBlur={() =>
                                  handleFormSubmit("blurAdd", `${field}`)
                                }
                              />
                            </RowCol>
                          </Row>

                          {(loan?.loanType === "BORROW") && (
                             <Row>
                                <RowCol span={3}>
                                </RowCol>

                            <RowCol span={21}>
                              <Form.DatePicker
                                rules={[
                                  {
                                    required: true,
                                    message: "请选择计息日期",
                                  },
                                ]}
                                type="date"
                                label="计息日期"
                                field={`${field}.borrowPlanStartDate`}
                                onBlur={() =>
                                  handleFormSubmit("blurAdd", `${field}`)
                                }
                                style={{ width: 120 }}
                              />
                            </RowCol>
                          </Row>)}
                          <Divider/>
                        </div>
                      ))}
                    </React.Fragment>
                  )}
                </ArrayField>
              </Form.Section>
            </RowCol>
            <RowCol span={14}>
              <Form.Section text={"还款计划"}>
                <Table
                  rowKey="id"
                  onRow={(record: any, index) => {
                    if (record.field) {
                      return {
                        style: {
                          backgroundColor: 'rgba(227, 249, 236, 0.6)',
                          borderLeft: '3px solid var(--semi-color-success)',
                        },
                      };
                    }
                    if (record.status === 'YQ') {
                      return {
                        style: {
                          backgroundColor: 'rgba(255, 236, 232, 0.4)',
                          borderLeft: '3px solid var(--semi-color-danger)',
                        },
                      };
                    }
                    if (record.key === 'summary') {
                      return {
                        style: {
                          backgroundColor: 'var(--semi-color-fill-0)',
                          fontWeight: 'bold',
                        },
                      };
                    }
                    return { style: {} };
                  }}
                  columns={[
                    {
                      title: "期号",
                      dataIndex: "periodNo",
                      render: (text: any, record: any) =>
                        record.key === "summary" ? (
                          <Typography.Title heading={5}>
                            {text}
                          </Typography.Title>
                        ) : (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            {text + "期"}
                            {record.field && <Tag color="green" size="small" type="solid">新增</Tag>}
                          </span>
                        ),
                    },

                    // {
                    //   title: "贷款金额",
                    //   dataIndex: "amount",
                    //   render: (text: number, record: any) =>
                    //     record.key === "summary" ? (
                    //       <Typography.Title heading={5}>
                    //         {formatAmountZhCn(text)}
                    //       </Typography.Title>
                    //     ) : (
                    //       formatAmountZhCn(text)
                    //     ),
                    // },
                    {
                      title: "应还金额",
                      dataIndex: "oneTermAmount",
                      render: (text: number, record: any) =>
                        record.key === "summary" ? (
                          <Typography.Title heading={5}>
                            {formatAmountZhCn(text)}
                          </Typography.Title>
                        ) : (
                          formatAmountZhCn(text)
                        ),
                    },
                    
                    { title: "应还日期", dataIndex: "dueDate" },
                    { title: "计息日期", dataIndex: "borrowPlanStartDate" },
                    { title: "还款人", dataIndex: "paidUserName" },
                    {
                      title: "还款金额",
                      dataIndex: "paidAmount",
                      render: (text: number, record: any) =>
                        record.key === "summary" ? (
                          <Typography.Title heading={5}>
                            {formatAmountZhCn(text?record.paidAmount:record.postingAmount)}
                          </Typography.Title>
                        ) : (
                          formatAmountZhCn(text?record.paidAmount:record.postingAmount)
                        ),
                    },
                    // { title: "过账金额", dataIndex: "postingAmount", render: (text: number, record: any) =>
                    //     record.key === "summary" ? (
                    //       <Typography.Title heading={5}>
                    //         {formatAmountZhCn(text)}
                    //       </Typography.Title>
                    //     ) : (
                    //       formatAmountZhCn(text)
                    //     )
                    // },
                    { title: "还款日期", dataIndex: "paidDate" },
                    { title: "还款方式", dataIndex: "paidMethod", render: renderPaidMethodTag},
                    {
                      title: "状态",
                      dataIndex: "status",
                      render: renderRepaymentPlanStatusTag
    
                    },
                  ]}
                  pagination={{ pageSize: 15 }}
                  dataSource={dataWithSummary}
                  bordered
                />
              </Form.Section>
            </RowCol>
          </Row>
        </Form>
      </Modal>
      <Modal
        icon={
          <IconModalStroked size={"extra-large"} style={{ color: "blue" }} />
        }
        title={"还款计划"}
        style={{ width: 1600, height: 700 }}
        headerStyle={{
          flexShrink: 0, // 关键属性 2：固定标题
          borderBottom: "1px solid var(--semi-color-border)",
        }}
        bodyStyle={{
          overflow: "auto", // 关键属性 3：启用内容滚动
          padding: "16px 24px",
          flex: 1,
        }}
        footer={null}
        hasCancel={false}
        onCancel={() => setPlanShow(false)}
        onOk={() => setPlanShow(false)}
        visible={planShow}
      >
        <div>
          <RepaymentPlan
            loanId={loanId}
            loanIds={loanIds}
            leadId={leadId}
            platform={platform}
            paidType={paidType}
          />
        </div>
      </Modal>

      {leadSummaryLoading && (
        <div style={{ padding: '16px 16px 8px' }}>
          <Skeleton.Title />
          <Skeleton.Paragraph rows={2} />
        </div>
      )}

      {!leadSummaryLoading && leadSummary && (
        <div style={{
          padding: '12px 16px',
          marginBottom: 8,
          backgroundColor: 'var(--semi-color-bg-2)',
          borderRadius: 8,
          boxShadow: 'var(--semi-shadow-elevated)',
        }}>
          {/* Row 1: Customer info */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Typography.Title heading={4} style={{ margin: 0 }}>
                {leadSummary.leadName}
              </Typography.Title>
              <Tag color="cyan" size="large" type="solid">ID: {leadSummary.leadId}</Tag>
              {leadSummary.leadCity && <Tag size="large">{leadSummary.leadCity}</Tag>}
              {leadSummary.loanStatus === 'BAD_DEPT' && <Tag color="purple" size="large" type="solid">已坏账</Tag>}
              {leadSummary.loanStatus === 'SETTLED' && <Tag color="grey" size="large" type="solid">已释放</Tag>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {leadSummary.agentNames && (
                <Text type="tertiary" style={{ fontSize: 13 }}>按揭: <Text strong>{leadSummary.agentNames}</Text></Text>
              )}
              {leadSummary.saleNames && (
                <Text type="tertiary" style={{ fontSize: 13 }}>销售: <Text strong>{leadSummary.saleNames}</Text></Text>
              )}
              <Button
                icon={<IconRefresh />}
                size="small"
                theme="borderless"
                onClick={() => {
                  setLeadSummaryLoading(true);
                  apiClient.request("/mortgageLoan/leadSummary", "GET", { leadId: props.leadId }).then((resp: any) => {
                    setLeadSummary(resp.data);
                    setLeadSummaryLoading(false);
                  });
                }}
              />
            </div>
          </div>

          {/* Row 2: Key financial metrics */}
          <div style={{ display: 'flex', gap: 0, marginBottom: 8 }}>
            {[
              { label: '总贷款金额', value: leadSummary.totalAmount, color: '#1677ff' },
              { label: '实还金额', value: leadSummary.totalPaidAmount, color: '#52c41a' },
              { label: '待还金额', value: (leadSummary.totalAmount ?? 0) - (leadSummary.totalPaidAmount ?? 0), color: '#fa8c16' },
              { label: '计划笔数', value: leadSummary.repaymentPlanCount , color: '#1677ff', isCount: true },
              { label: '已还笔数', value: leadSummary.repaymentCount , color: '#52c41a', isCount: true },
              { label: '未还笔数', value: (leadSummary.repaymentPlanCount ?? 0) - (leadSummary.repaymentCount ?? 0), color: '#e91e63', isCount: true },
              { label: '出款金额', value: (leadSummary.totalHelpPaidAmount ?? 0) + (leadSummary.totalCardHelpPaidAmount ?? 0)+ (leadSummary.totalBorrowAmount ?? 0), color: '#722ed1', isCount: true },
              { label: '代过账金额', value: (leadSummary.totalHelpPostingAmount ?? 0) , color: '#722ed1', isCount: true },
              { label: '支出金额', value: leadSummary.totalExpense, color: '#722ed1' },
            ].map((item) => (
              <div key={item.label} style={{
                flex: '1 1 0', minWidth: 120, padding: '10px 14px',
                background: 'var(--semi-color-bg-1)', borderRadius: 4,
                textAlign: 'center', borderLeft: `3px solid ${item.color}`,
                borderRight: '1px solid var(--semi-color-bg-0)',
              }}>
                <div style={{ fontSize: 11, color: 'var(--semi-color-text-2)', marginBottom: 4 }}>{item.label}</div>
                <div style={{ fontSize: 18, fontWeight: 'bold', color: item.color }}>
                  {item.isCount ? (item.value || 0) : formatAmountZhCn(item.value || 0)}
                </div>
              </div>
            ))}
          </div>

          {/* Row 3: Loan type breakdown */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <Text type="tertiary" style={{ marginLeft: 12, fontSize: 12 }}>
              贷款数量： 
            </Text>
            <Tag color="cyan" size="large" type="solid">平台数 {leadSummary.platformCount ?? 0}</Tag>
            <Tag color="blue" size="large" type="solid">总贷款数 {leadSummary.totalMortgageCount ?? 0}</Tag>
            <Tag color="blue" size="large">信贷 {leadSummary.loanCount ?? 0}</Tag>
            <Tag color="violet" size="large">信用卡 {leadSummary.cardCount ?? 0}</Tag>
            <Tag color="orange" size="large">借款 {leadSummary.borrowCount ?? 0}</Tag>
            <Text type="tertiary" style={{ marginLeft: 12, fontSize: 12 }}>
              贷款金额： 
            </Text>
            <Tag color="blue" size="large">信贷 {formatAmountZhCn(leadSummary.totalLoanAmount ?? 0)}</Tag>
            <Tag color="violet" size="large">信用卡 {formatAmountZhCn(leadSummary.totalCardAmount ?? 0)}</Tag>
            <Tag color="orange" size="large">借款 {formatAmountZhCn(leadSummary.totalBorrowAmount ?? 0)}</Tag>
          </div>
        </div>
      )}

      {!leadSummaryLoading && !leadSummary && props.leadId && (
        <div style={{ padding: '24px 16px', marginBottom: 8 }}>
          <Empty title="暂无客户贷款汇总数据" description="该客户暂无贷款记录" />
        </div>
      )}
  
      <Crud
        getCrudApi={(crudApi) => {
          setCrudApi(crudApi);
        }}
        canUpdate={false}
        canCreate={false}
        canDelete={false}
        name="按揭贷款"
        entityKey="mortgageLoan/listGroupPlatform"
        pageSize={500}
        params={{ leadId: props.leadId }}
        treeTable={true}
        getRowActions={(a) => {
          let actions = [];
          if (a.platform == "汇总") {
            return [];
          }
          //------------------------------------------------按钮控制------------------------------------------------------------------
          let isManager =
            role == "agent" ||
            role == "agent_manager" ||
            role == "op_manager" ||
            role == "monitor" ||
            role == "root";

          if (isManager) {
            actions.push(editAction);
            actions.push(deleteAction);
            actions.push(makePlanAction);
          }

          return actions;
        }}
        cols={cols}
      />
    </>
  );
}
